import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { 
  Tags, 
  Plus, 
  Trash2, 
  Edit2,
  AlertCircle,
  Loader2,
  RefreshCw,
  Download,
  Server,
  Check,
  X,
  Palette,
  Key,
  FileText,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, exportToCSV } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_COLORS = [
  '#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16',
  '#22C55E', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9',
  '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF',
  '#EC4899', '#F43F5E', '#78716C', '#6B7280', '#64748B'
];

export function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncData, setSyncData] = useState(null);
  const [hostsByCategory, setHostsByCategory] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [formData, setFormData] = useState({
    name: '',
    keywords: '',
    color: '#6366F1',
    description: '',
    zabbix_tag_value: ''
  });
  const { getAuthHeader, user } = useAuthStore();

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`, { headers: getAuthHeader() });
      const data = response.data;
      if (data.categories) {
        setCategories(data.categories);
        setIsDefault(data.is_default || false);
      } else if (Array.isArray(data)) {
        setCategories(data);
        setIsDefault(false);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  const fetchHostsByCategory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories/hosts`, { headers: getAuthHeader() });
      setHostsByCategory(response.data);
    } catch (err) {
      console.error('Error fetching hosts by category:', err);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchCategories();
    fetchHostsByCategory();
  }, [fetchCategories, fetchHostsByCategory]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.get(`${API}/categories/sync-zabbix`, { headers: getAuthHeader() });
      setSyncData(response.data);
      toast.success('Sincronização concluída');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na sincronização');
    } finally {
      setSyncing(false);
    }
  };

  const handleImportFromZabbix = async () => {
    try {
      const response = await axios.post(`${API}/categories/import-from-zabbix`, {}, { headers: getAuthHeader() });
      toast.success(response.data.message);
      fetchCategories();
      fetchHostsByCategory();
      setSyncData(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao importar');
    }
  };

  const handleInitDefaults = async () => {
    if (!window.confirm('Isso criará as categorias padrão. Continuar?')) return;
    
    try {
      const response = await axios.post(`${API}/categories/init-defaults`, {}, { headers: getAuthHeader() });
      toast.success(response.data.message);
      fetchCategories();
      fetchHostsByCategory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar categorias padrão');
    }
  };

  const openDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        keywords: (category.keywords || []).join(', '),
        color: category.color || '#6366F1',
        description: category.description || '',
        zabbix_tag_value: category.zabbix_tag_value || ''
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        keywords: '',
        color: PRESET_COLORS[Math.floor(Math.random() * PRESET_COLORS.length)],
        description: '',
        zabbix_tag_value: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome da categoria é obrigatório');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k),
        color: formData.color,
        description: formData.description.trim() || null,
        zabbix_tag_value: formData.zabbix_tag_value.trim() || null
      };

      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, payload, { headers: getAuthHeader() });
        toast.success('Categoria atualizada');
      } else {
        await axios.post(`${API}/categories`, payload, { headers: getAuthHeader() });
        toast.success('Categoria criada');
      }

      setDialogOpen(false);
      fetchCategories();
      fetchHostsByCategory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Excluir categoria "${category.name}"?`)) return;

    try {
      await axios.delete(`${API}/categories/${category.id}`, { headers: getAuthHeader() });
      toast.success('Categoria excluída');
      fetchCategories();
      fetchHostsByCategory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const toggleExpanded = (catName) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(catName)) {
        newSet.delete(catName);
      } else {
        newSet.add(catName);
      }
      return newSet;
    });
  };

  const handleExportCSV = () => {
    const columns = [
      { key: 'name', label: 'Categoria' },
      { key: 'keywords', label: 'Palavras-chave' },
      { key: 'zabbix_tag_value', label: 'Tag Zabbix' },
      { key: 'color', label: 'Cor' },
      { key: 'description', label: 'Descrição' }
    ];
    
    const data = categories.map(c => ({
      ...c,
      keywords: (c.keywords || []).join(', ')
    }));
    
    exportToCSV(data, columns, 'categorias');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="categories-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Tags className="h-5 w-5" />
            Gerenciamento de Categorias
          </h2>
          <p className="text-sm text-muted-foreground">
            Configure categorias para classificação automática de hosts
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
              {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Sincronizar Zabbix
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-2" />
              Exportar CSV
            </Button>
            <Button onClick={() => openDialog()} data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Categoria
            </Button>
          </div>
        )}
      </div>

      {/* Sync Data Alert */}
      {syncData && (
        <Alert className="bg-blue-50 dark:bg-blue-950 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="flex items-center justify-between">
            <div>
              <strong>Sincronização com Zabbix:</strong> {syncData.total_hosts_scanned} hosts analisados, 
              {' '}{syncData.categories_found_in_zabbix?.length || 0} categorias encontradas nas tags.
              {syncData.new_categories?.length > 0 && (
                <span className="text-blue-600 ml-2">
                  {syncData.new_categories.length} nova(s) categoria(s) disponível(is) para importar.
                </span>
              )}
            </div>
            {syncData.new_categories?.length > 0 && (
              <Button size="sm" onClick={handleImportFromZabbix}>
                Importar Novas
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="categories">
        <TabsList>
          <TabsTrigger value="categories">Categorias</TabsTrigger>
          <TabsTrigger value="hosts">Hosts por Categoria</TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          {/* Default categories alert */}
          {isDefault && categories.length > 0 && isAdmin && (
            <Alert className="bg-amber-50 dark:bg-amber-950 border-amber-200">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="flex items-center justify-between">
                <div>
                  <strong>Categorias padrão:</strong> Estas categorias são modelos automáticos. 
                  Inicialize-as para poder editar, excluir e personalizar.
                </div>
                <Button size="sm" onClick={handleInitDefaults} className="ml-4 shrink-0">
                  Inicializar Categorias
                </Button>
              </AlertDescription>
            </Alert>
          )}
          
          {categories.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Tags className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium">Nenhuma categoria cadastrada</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Crie categorias personalizadas ou importe do Zabbix
                </p>
                {isAdmin && (
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={handleInitDefaults}>
                      Criar Categorias Padrão
                    </Button>
                    <Button onClick={handleSync}>
                      Sincronizar com Zabbix
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {categories.map(category => {
                const hostCount = hostsByCategory?.categories?.find(c => c.name === category.name)?.hosts_count || 0;
                
                return (
                  <Card key={category.id || category.name} data-testid={`category-card-${category.name}`}>
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: category.color }}
                          />
                          <CardTitle className="text-base">{category.name}</CardTitle>
                        </div>
                        {isAdmin && category.id && (
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => openDialog(category)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDelete(category)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                      {category.description && (
                        <CardDescription className="text-xs">{category.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Host count */}
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          <strong>{hostCount}</strong> hosts
                        </span>
                      </div>

                      {/* Keywords */}
                      {category.keywords?.length > 0 && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                            <Key className="h-3 w-3" /> Palavras-chave:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {category.keywords.slice(0, 5).map((keyword, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {keyword}
                              </Badge>
                            ))}
                            {category.keywords.length > 5 && (
                              <Badge variant="outline" className="text-xs">
                                +{category.keywords.length - 5}
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Zabbix Tag */}
                      {category.zabbix_tag_value && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <FileText className="h-3 w-3" />
                          Tag Zabbix: <code className="bg-muted px-1 rounded">{category.zabbix_tag_value}</code>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Hosts by Category Tab */}
        <TabsContent value="hosts" className="space-y-4">
          {hostsByCategory ? (
            <>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Total: <strong>{hostsByCategory.total_hosts}</strong> hosts</span>
                <span>Categorizados: <strong>{hostsByCategory.categorized}</strong></span>
                <span>Não categorizados: <strong>{hostsByCategory.total_hosts - hostsByCategory.categorized}</strong></span>
              </div>

              <div className="space-y-2">
                {hostsByCategory.categories?.map(cat => (
                  <Card key={cat.name}>
                    <button
                      onClick={() => toggleExpanded(cat.name)}
                      className="w-full p-4 flex items-center justify-between hover:bg-accent/50 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {expandedCategories.has(cat.name) ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="font-medium">{cat.name}</span>
                      </div>
                      <Badge variant="secondary">{cat.hosts_count} hosts</Badge>
                    </button>
                    
                    {expandedCategories.has(cat.name) && cat.hosts && (
                      <div className="px-4 pb-4">
                        <ScrollArea className="h-[200px]">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-2 px-2">Host</th>
                                <th className="text-left py-2 px-2">Grupo</th>
                                <th className="text-left py-2 px-2">Match</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cat.hosts.map((host, i) => (
                                <tr key={host.hostid || i} className="border-b border-muted">
                                  <td className="py-2 px-2">{host.name}</td>
                                  <td className="py-2 px-2 text-muted-foreground">
                                    {host.groups?.slice(0, 2).join(', ')}
                                  </td>
                                  <td className="py-2 px-2">
                                    <Badge 
                                      variant="outline" 
                                      className={cn(
                                        "text-xs",
                                        host.match_type === 'zabbix_tag' && "bg-green-50 text-green-700 border-green-200",
                                        host.match_type === 'keyword_name' && "bg-blue-50 text-blue-700 border-blue-200",
                                        host.match_type === 'keyword_group' && "bg-purple-50 text-purple-700 border-purple-200",
                                        host.match_type === 'none' && "bg-gray-50 text-gray-700 border-gray-200"
                                      )}
                                    >
                                      {host.match_type === 'zabbix_tag' && 'Tag Zabbix'}
                                      {host.match_type === 'keyword_name' && 'Nome'}
                                      {host.match_type === 'keyword_group' && 'Grupo'}
                                      {host.match_type === 'none' && 'Sem match'}
                                      {host.match_type === 'zabbix_tag_unregistered' && 'Tag (não registrada)'}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </ScrollArea>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin" />
              Carregando hosts...
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
            </DialogTitle>
            <DialogDescription>
              Configure os parâmetros para classificação automática de hosts
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label>Nome da Categoria *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Servidores de Produção"
                data-testid="category-name-input"
              />
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Palette className="h-4 w-4" /> Cor
              </Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData({ ...formData, color })}
                    className={cn(
                      "w-8 h-8 rounded-full transition-all",
                      formData.color === color && "ring-2 ring-offset-2 ring-primary"
                    )}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            {/* Keywords */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Key className="h-4 w-4" /> Palavras-chave
              </Label>
              <Input
                value={formData.keywords}
                onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                placeholder="servidor, linux, docker, nginx (separadas por vírgula)"
              />
              <p className="text-xs text-muted-foreground">
                Hosts serão categorizados se nome, grupo ou <strong>qualquer valor de tag</strong> contiver estas palavras
              </p>
            </div>

            {/* Zabbix Tag Value */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <FileText className="h-4 w-4" /> Valor Exato de Tag Zabbix
              </Label>
              <Input
                value={formData.zabbix_tag_value}
                onChange={(e) => setFormData({ ...formData, zabbix_tag_value: e.target.value })}
                placeholder="Ex: PRODUCAO, Firewall, Docker"
              />
              <p className="text-xs text-muted-foreground">
                Mapeia hosts com qualquer tag que tenha exatamente este valor (ex: Ambiente: PRODUCAO)
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCategory ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
